import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import CancelIcon from "@mui/icons-material/Cancel";
import { Card, Chip } from "@mui/material";
import { useState } from "react";

export const PracticeSetupFormPart1 = ({
  data,
  setData,
  formErrors,
  setFormErrors,
}) => {
  const getValue = (businessKey, legacyKey, fallback = "") =>
    data?.[businessKey] ?? data?.[legacyKey] ?? fallback;
  const getError = (businessKey, legacyKey) =>
    formErrors?.[businessKey] || formErrors?.[legacyKey];
  const setBusinessField = (businessKey, legacyKey, value) => {
    setData((prev) => ({
      ...prev,
      [businessKey]: value,
      [legacyKey]: value,
      ...(businessKey === "businessType" || legacyKey === "practiceType"
        ? { businessNiches: [], practiceNiches: [] }
        : {}),
    }));
  };

  const practiceTypeToNiches = {
    Chiropractic: [
      "Adjustment-only",
      "Family / wellness",
      "Sports-focused",
      "Heavily therapy-focused",
      "Neuropathy",
      "Hybrid (adjustments + therapies/services)",
    ],
    Dentistry: [
      "General",
      "Cosmetic",
      "Orthodontics",
      "Pediatric",
      "Periodontics / Endodontics",
      "Oral surgery / Implants",
    ],
    "Functional / Integrative Medicine": [
      "General functional medicine",
      "Hormone & thyroid health",
      "Gut & nutrition-focused",
      "Autoimmune / chronic illness",
      "Weight loss & metabolic health",
      "Anti-aging / longevity / IV therapy",
    ],
    "Physical Therapy / Rehab": [
      "Orthopedic / sports injury",
      "Post-surgical",
      "Pediatric / geriatric",
      "Neurological",
      "Pelvic floor",
    ],
    "Massage / Bodywork": [
      "Relaxation / spa",
      "Sports / deep tissue",
      "Medical massage",
      "Prenatal",
      "Myofascial release",
    ],
    "Acupuncture / Traditional Chinese Medicine": [
      "General acupuncture",
      "Pain management",
      "Fertility-focused",
      "Stress / anxiety",
      "Cosmetic",
    ],
    "Wellness / Med Spa": [
      "Aesthetic (Botox, fillers, facials, laser)",
      "Weight loss & hormones",
      "IV therapy / vitamin infusions",
      "Longevity & biohacking",
    ],
    "Primary Care / Concierge": [
      "Direct primary care",
      "Concierge / membership model",
      "Preventive & wellness",
      "Chronic care management",
      "Telemedicine-first",
    ],
    "Other": [
      "Nutrition & dietetics",
      "Naturopathy",
      "Occupational / speech therapy",
      "Podiatry",
      "Mental health / psychology",
    ],
  };

  const practiceTypes = Object.keys(practiceTypeToNiches);
  const selectedBusinessType = getValue("businessType", "practiceType");
  const selectedBusinessNiches = getValue("businessNiches", "practiceNiches", []);
  const selectedBusinessName = getValue("businessName", "practiceName");
  const currentNiches = selectedBusinessType
    ? practiceTypeToNiches[selectedBusinessType]
    : [];

  const handleChange = (field, value) => {
    setBusinessField(field, field.replace("business", "practice"), value);

    if (value && value.toString().trim() !== "") {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
        [field.replace("business", "practice")]: "",
      }));
    }
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        columnGap: 2,
        rowGap: 2,
        width: "100%",
        margin: "0 auto",
        mt: "24px",
      }}
    >
      {/* Business Name */}
      <Box sx={{ minHeight: "64px" }}>
        <Typography
          variant="body2"
          fontWeight={600}
          mb={1}
          color="text.primary"
          sx={{ fontFamily: "Rethink Sans" }}
        >
          Business Name
        </Typography>
        <TextField
          fullWidth
          placeholder="American Health Center"
          variant="outlined"
          value={selectedBusinessName}
          onChange={(e) => handleChange("businessName", e.target.value)}
          error={!!getError("businessName", "practiceName")}
          helperText={getError("businessName", "practiceName")}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "40px",
              borderRadius: "8px",
              "& fieldset": { borderColor: "#E0E0E0" },
              "&.Mui-focused fieldset": { borderColor: "primary.main" },
            },
            "& input": { padding: "8px 12px", fontSize: "14px" },
          }}
        />
      </Box>

      <Box sx={{ minHeight: "64px" }}>
        <Typography
          variant="body2"
          fontWeight={600}
          mb={1}
          color="text.primary"
          sx={{ fontFamily: "Rethink Sans" }}
        >
          Business Type
        </Typography>
        <Select
          fullWidth
          value={selectedBusinessType}
          onChange={(e) => handleChange("businessType", e.target.value)}
          displayEmpty
          autoWidth={false}
          MenuProps={{
            disablePortal: true,
            PaperProps: {
              sx: {
                mt: "8px",
                borderRadius: "8px",
                boxShadow: "0px 4px 20px 0px #0000003D",
                p: "10px 12px",
                width: "max-content",
                minWidth: "100%",
                maxWidth: "100px !important",
                maxHeight: "28vh",
                fontFamily: "Rethink Sans",
              },
            },
            MenuListProps: {
              sx: {
                py: 0,
                gap: "12px",
                "& .MuiMenuItem-root": {
                  gap: "12px",
                  fontFamily: "Rethink Sans",
                  fontSize: "0.875rem",
                  borderRadius: "8px",
                  p: "10px 12px",
                  mb: "10px",
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.4,
                  "&:last-of-type": { mb: 0 },
                  "&:hover": { backgroundColor: "rgba(83, 109, 236, 0.08)" },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(83, 109, 236, 0.08)",
                    "&:hover": { backgroundColor: "rgba(83, 109, 236, 0.12)" },
                  },
                  "&.Mui-focusVisible": {
                    backgroundColor: "rgba(83, 109, 236, 0.12)",
                  },
                },
              },
            },
          }}
          sx={{
            height: "40px",
            borderRadius: "8px",
            fontFamily: "Rethink Sans",
            fontSize: "0.875rem",
            color: "#212121",
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: !!getError("businessType", "practiceType")
                ? "red"
                : "#E0E0E0",
              borderWidth: "1px !important",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#536DEC",
              borderWidth: "2px !important",
            },
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              height: "100%",
              fontFamily: "Rethink Sans",
              fontSize: "0.875rem",
              paddingY: "8px",
              color: selectedBusinessType ? "#212121" : "#9E9E9E",
            },
          }}
          renderValue={(selected) =>
            selected ? (
              selected
            ) : (
              <span style={{ color: "#9E9E9E" }}>Select Option</span>
            )
          }
        >
          {practiceTypes.map((type) => (
            <MenuItem
              sx={{ fontFamily: "Rethink Sans" }}
              key={type}
              value={type}
            >
              {type}
            </MenuItem>
          ))}
        </Select>
        {getError("businessType", "practiceType") && (
          <Typography
            sx={{ fontFamily: "Rethink Sans" }}
            variant="caption"
            color="error"
          >
            {getError("businessType", "practiceType")}
          </Typography>
        )}
      </Box>

      {selectedBusinessType && (
        <Box
          sx={{ gridColumn: { xs: "span 1", md: "span 2" }, minHeight: "64px" }}
        >
          <Typography
            variant="body2"
            fontWeight={600}
            mb={1}
            color="text.primary"
            sx={{ fontFamily: "Rethink Sans" }}
          >
            Business Niches
          </Typography>
          <Select
            multiple
            fullWidth
            value={selectedBusinessNiches}
            onChange={(e) => handleChange("businessNiches", e.target.value)}
            displayEmpty
            MenuProps={{
              disablePortal: true,
              PaperProps: {
                sx: {
                  mt: "8px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 20px 0px #0000003D",
                  maxHeight: "18vh",
                },
              },
              MenuListProps: {
                sx: {
                  py: 0,
                  "& .MuiMenuItem-root": {
                    fontFamily: "Rethink Sans",
                    fontSize: "0.875rem",
                    borderRadius: "8px",
                    my: "10px",
                    mx: "12px",
                    p: 0,
                    "&:hover": { backgroundColor: "white" },
                    "&.Mui-selected": {
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "white" },
                    },
                    "&.Mui-focusVisible": {
                      backgroundColor: "white",
                    },
                  },
                },
              },
            }}
            sx={{
              height: "40px",
              borderRadius: "8px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: !!getError("businessNiches", "practiceNiches")
                  ? "red"
                  : "#E0E0E0",
                borderWidth: "1px !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#536DEC",
                borderWidth: "2px !important",
              },
              "& .MuiSelect-select": {
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "4px",
                height: "100%",
                fontFamily: "Rethink Sans",
                fontSize: "0.875rem",
                paddingY: "8px",
                color: selectedBusinessNiches?.length ? "#212121" : "#9E9E9E",
              },
            }}
            renderValue={(selected) =>
              selected?.length ? (
                <Box
                  sx={{
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                    fontFamily: "Rethink Sans",
                    fontSize: "0.875rem",
                    color: "#212121",
                  }}
                  title={selected.join(", ")}
                >
                  {selected.join(", ")}
                </Box>
              ) : (
                <span style={{ color: "#9E9E9E" }}>Select Option</span>
              )
            }
          >
            {currentNiches.map((niche) => (
              <MenuItem key={niche} value={niche}>
                <Checkbox
                  checked={selectedBusinessNiches?.includes(niche)}
                  sx={{
                    color: "#9E9E9E",
                    "&.Mui-checked": { color: "#536DEC" },
                  }}
                />
                <ListItemText
                  primary={niche}
                  slotProps={{
                    primary: {
                      sx: {
                        fontFamily: "Rethink Sans",
                        fontSize: "0.875rem",
                        color: "#212121",
                      },
                    },
                  }}
                />
              </MenuItem>
            ))}
          </Select>
          {getError("businessNiches", "practiceNiches") && (
            <Typography
              sx={{ fontFamily: "Rethink Sans" }}
              variant="caption"
              color="error"
            >
              {getError("businessNiches", "practiceNiches")}
            </Typography>
          )}
        </Box>
      )}

      {[
        {
          label: "Address Line 1",
          field: "address1",
          placeholder: "Street Name",
          type: "text",
          autoComplete: "address-line1",
        },
        {
          label: "Address Line 2 (Optional)",
          field: "address2",
          placeholder: "",
          type: "text",
          autoComplete: "address-line2",
        },
        {
          label: "City",
          field: "city",
          placeholder: "Des Moines",
          type: "text",
          autoComplete: "address-level2",
        },
        {
          label: "ZIP Code",
          field: "zip",
          placeholder: "50012",
          type: "text", // ← MUST be text or autofill won't trigger
          autoComplete: "postal-code",
        },
      ].map(({ label, field, placeholder, type, autoComplete }) => (
        <Box key={field} sx={{ minHeight: "64px" }}>
          <Typography
            variant="body2"
            fontWeight={600}
            mb={1}
            sx={{ fontFamily: "Rethink Sans" }}
            color="text.primary"
          >
            {label}
          </Typography>

          <TextField
            fullWidth
            type={type}
            placeholder={placeholder}
            variant="outlined"
            value={data[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            error={!!formErrors[field]}
            helperText={formErrors[field]}
            autoComplete={autoComplete}
            sx={{
              fontFamily: "Rethink Sans",
              "& .MuiOutlinedInput-root": {
                height: "40px",
                borderRadius: "8px",
                "& fieldset": { borderColor: "#E0E0E0" },
                "&.Mui-focused fieldset": { borderColor: "primary.main" },
              },
              "& input": { padding: "8px 12px", fontSize: "14px" },
            }}
          />
        </Box>
      ))}
    </Box>
  );
};

const modes = [
  {
    title: "Growth",
    description: "Expanding income while protecting profitability.",
    icon: "/Tree Growth Streamline UX Duotone.svg",
  },
  {
    title: "Sustainability",
    description: "Improving efficiency and long-term profits.",
    icon: "/Protect Eco 1 Streamline UX Duotone.svg",
  },
  {
    title: "Selling",
    description: "Preparing the business for sale in the next 12–24 months.",
    icon: "/Selling Idea 1 Streamline UX Duotone.svg",
  },
  {
    title: "Start-Up",
    description: "Laying the foundation and building momentum.",
    icon: "/Startup People 2 Streamline UX Duotone.svg",
  },
  {
    title: "Turnaround",
    description: "Fixing underperformance and restoring profitability.",
    icon: "/Screwdriver Wrench Streamline UX Duotone.svg",
  },
  {
    title: "Exit-Planning",
    description: "Laying the groundwork for stepping out over 3–5 years.",
    icon: "/Growth Arrows Streamline UX Duotone.svg",
  },
];

export const PracticeSetupFormPart2 = ({
  data,
  setData,
  formErrors,
  setFormErrors,
}) => {
  const selectedBusinessMode = data.businessMode ?? data.practiceMode ?? "";
  const modeError = formErrors.businessMode || formErrors.practiceMode;
  const [selected, setSelected] = useState(
    selectedBusinessMode
      ? modes.findIndex((m) => m.title === selectedBusinessMode)
      : null
  );

  const handleSelect = (index) => {
    setSelected(index);
    setData((prev) => ({
      ...prev,
      businessMode: modes[index].title,
      practiceMode: modes[index].title,
    }));
    setFormErrors((prev) => ({
      ...prev,
      businessMode: false,
      practiceMode: false,
    })); // clear error
  };

  return (
    <Box sx={{ width: "100%", mt: "24px", fontFamily: "Rethink Sans" }}>
      <Typography
        variant="body1"
        mb={"24px"}
        sx={{
          fontFamily: "Rethink Sans",
          fontWeight: "fontWeightRegular",
          fontSize: "14px",
          lineHeight: "150%",
          letterSpacing: "0.1px",
        }}
      >
        Please select one of the business modes below that resonates with your
        clinic.
      </Typography>

      <Box
        display="grid"
        gridTemplateColumns={{
          xs: "1fr",
          sm: "1fr 1fr",
          md: "repeat(3, 1fr)",
        }}
        gap={"16px"}
        justifyContent="center"
      >
        {modes.map((mode, index) => {
          const isSelected = selected === index;
          return (
            <Box key={index} sx={{ minHeight: "200px", display: "flex" }}>
              <Card
                onClick={() => handleSelect(index)}
                elevation={0}
                sx={{
                  width: "100%",
                  minHeight: "200px",
                  flex: 1,
                  borderRadius: "8px",
                  backgroundColor: isSelected ? "#EEF0FD80" : "white",
                  border: isSelected
                    ? "1px solid #536DEC"
                    : "1px solid rgba(18, 22, 33, 0.08)",
                  p: 2,
                  cursor: "pointer",
                  boxShadow: isSelected
                    ? "0 4px 12px rgba(0,0,0,0.10) !important"
                    : "none",
                  transition:
                    "box-shadow 180ms ease, transform 180ms ease, border-color 180ms ease",
                  "&:hover": {
                    boxShadow: "0 4px 12px rgba(0,0,0,0.10) !important",
                    transform: "translateY(-2px)",
                  },
                  "&:focus": {
                    outline: "none",
                  },
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  height="100%"
                  gap={1}
                >
                  <Box
                    component="img"
                    src={mode.icon}
                    alt={`${mode.title} icon`}
                    sx={{
                      width: "80px",
                      height: "80px",
                    }}
                  />

                  <Typography
                    variant="h6"
                    fontWeight={600}
                    textAlign="center"
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {mode.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                    sx={{
                      fontSize: "13px",
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                    }}
                  >
                    {mode.description}
                  </Typography>
                </Box>
              </Card>
            </Box>
          );
        })}
      </Box>

      {modeError && (
        <Typography
          variant="body2"
          color="error"
          mt={1}
          sx={{ textAlign: "center" }}
        >
          Please select a business mode.
        </Typography>
      )}
    </Box>
  );
};

export const PracticeSetupFormPart3 = ({ data, setData }) => {
  const selectedBusinessMode = data.businessMode ?? data.practiceMode ?? "";
  const selectedBusinessNiches = data.businessNiches ?? data.practiceNiches ?? [];
  const selectedBusinessName = data.businessName ?? data.practiceName ?? "";
  const selectedBusinessType = data.businessType ?? data.practiceType ?? "";
  const Label = ({ children }) => (
    <Typography
      variant="body2"
      mb={0.5}
      sx={{
        fontFamily: "Rethink Sans",
        fontWeight: 600,
        fontSize: "0.875rem",
        lineHeight: "157%",
        letterSpacing: "0.1px",
        color: "var(--text-primary, #000000DE)",
      }}
    >
      {children}
    </Typography>
  );

  const Value = ({ children }) => (
    <Typography
      variant="body2"
      sx={{
        fontFamily: "Rethink Sans",
        fontSize: "1rem",
        lineHeight: "150%",
        letterSpacing: "0.15px",
        color: "var(--text-primary, #000000DE)",
      }}
    >
      {children}
    </Typography>
  );

  const selectedMode = modes.find((m) => m.title === selectedBusinessMode);
  const practiceModeValue = selectedMode
    ? `${selectedMode.title} - ${selectedMode.description}`
    : selectedBusinessMode || "";

  const handleRemoveNiche = (niche) => {
    if (selectedBusinessNiches.length > 1) {
      setData({
        ...data,
        businessNiches: selectedBusinessNiches.filter((n) => n !== niche),
        practiceNiches: selectedBusinessNiches.filter((n) => n !== niche),
      });
    }
  };

  const fields = [
    { label: "Business Name", value: selectedBusinessName },
    { label: "Business Type", value: selectedBusinessType },
    {
      label: "Business Niches",
      value: selectedBusinessNiches,
      isChips: true,
    },
    {
      label: "Address",
      value: `${data.address1}${data.address2 ? `, ${data.address2}` : ""}`,
    },
    { label: "City", value: data.city },
    { label: "ZIP Code", value: data.zip },
    { label: "Business Mode", value: practiceModeValue },
  ];

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        pt: "24px",
        maxWidth: 700,
        mx: "auto",
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr",
          md: "2fr 1fr",
        },
        rowGap: 2,
        columnGap: 4,
      }}
    >
      {fields.map((field) => (
        <Box key={field.label}>
          <Label>{field.label}</Label>
          {field.isChips ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                width: "100%",
                minWidth: 0,
                overflowWrap: "break-word",
                wordBreak: "break-word",
              }}
            >
              {field.value.map((niche) => (
                <Chip
                  key={niche}
                  label={niche}
                  onDelete={
                    field.value.length > 1
                      ? () => handleRemoveNiche(niche)
                      : undefined
                  }
                  deleteIcon={
                    field.value.length > 1 ? <CancelIcon /> : undefined
                  }
                  sx={{
                    maxWidth: "100%",
                    whiteSpace: "normal !important",
                    wordBreak: "break-word !important",
                    lineHeight: "1.2",
                    "& .MuiChip-label": {
                      whiteSpace: "normal !important",
                      wordBreak: "break-word",
                      textAlign: "left",
                    },
                  }}
                />
              ))}
            </Box>
          ) : (
            <Value>{field.value}</Value>
          )}
        </Box>
      ))}
    </Box>
  );
};
